/** "Was this article helpful?" — the reader's own answer.
 *
 * The portal shows somebody the state of **their own** thumb, never a score.
 * Counts are the editor's question and live on the agent KB screen; a reader
 * being shown that 80% of people disagreed with them is a different product.
 *
 * The endpoint is a `PUT`, which is why re-clicking the same answer is safe:
 * there is at most one answer per person per article, enforced by a unique
 * constraint, so a double-submit from a slow connection cannot make two votes.
 */

/** `true` helpful · `false` not helpful · `null` not answered. */
export type Answer = boolean | null

export interface FeedbackResponse {
  my_feedback: Answer
}

export type FeedbackState = 'unanswered' | 'saving' | 'saved' | 'error'

/** What the widget should render. One decision, so the states cannot overlap.
 *
 * `saving` outranks `error` deliberately: once a retry is in flight, showing
 * the previous failure alongside a spinner reads as a second, new failure.
 */
export function feedbackState(query: {
  isPending: boolean
  isError: boolean
  answer: Answer
}): FeedbackState {
  if (query.isPending) return 'saving'
  if (query.isError) return 'error'
  return query.answer === null ? 'unanswered' : 'saved'
}

/** Whether a button should read as chosen. */
export function isChosen(answer: Answer, value: boolean): boolean {
  return answer === value
}

/** What clicking a button should send.
 *
 * Clicking the answer you already gave withdraws it, rather than being a no-op:
 * the button is the only control there is, and somebody who clicked by accident
 * needs a way back. `null` means "delete my answer".
 */
export function nextAnswer(current: Answer, clicked: boolean): Answer {
  return current === clicked ? null : clicked
}

/** The sentence under the buttons. */
export function feedbackMessage(state: FeedbackState, answer: Answer): string {
  switch (state) {
    case 'saving':
      return 'Saving…'
    case 'error':
      return 'That could not be saved. Try again.'
    case 'saved':
      return answer
        ? 'Thanks — recorded as helpful. Click again to undo.'
        : 'Thanks — recorded as not helpful. Click again to undo.'
    default:
      return 'Your answer helps the team find articles worth rewriting.'
  }
}

/* ------------------------------------------------------- the editor's score */

export interface ArticleScore {
  helpful: number
  not_helpful: number
  total: number
  /** Null when nobody has answered — silence is not a score of nought. */
  percentage: number | null
}

/** The helpful score as the editor's tile shows it, or null when there is none.
 *
 * Returning null rather than "0%" is the whole point: an article nobody has
 * rated is not an article everybody disliked, and a tile that could not tell
 * them apart would send an editor to rewrite the wrong page.
 */
export function scoreLabel(score: ArticleScore | undefined): string | null {
  if (!score || score.total === 0) return null
  return `${score.percentage}%`
}

export function scoreHint(score: ArticleScore | undefined): string {
  if (!score || score.total === 0) return 'No responses yet'
  const { helpful, not_helpful: notHelpful, total } = score
  return `${helpful} helpful · ${notHelpful} not · ${total} response${total === 1 ? '' : 's'}`
}

/** Articles an editor should look at first: rated, and rated badly.
 *
 * Unrated articles are excluded rather than sorted to the bottom — they carry
 * no signal at all, and mixing them into a "least helpful" list would bury the
 * ones readers actually complained about.
 */
export function leastHelpful<T extends { feedback?: ArticleScore }>(
  articles: T[],
  limit = 5,
): T[] {
  return articles
    .filter((article) => (article.feedback?.total ?? 0) > 0)
    .sort((a, b) => (a.feedback!.percentage ?? 0) - (b.feedback!.percentage ?? 0))
    .slice(0, limit)
}

/** The whole KB's score, for the dashboard tile. */
export function overallScore(articles: { feedback?: ArticleScore }[]): ArticleScore {
  const totals = articles.reduce(
    (sum, article) => ({
      helpful: sum.helpful + (article.feedback?.helpful ?? 0),
      not_helpful: sum.not_helpful + (article.feedback?.not_helpful ?? 0),
    }),
    { helpful: 0, not_helpful: 0 },
  )
  const total = totals.helpful + totals.not_helpful
  return {
    ...totals,
    total,
    percentage: total ? Math.round((totals.helpful * 100) / total) : null,
  }
}
